(function () {
  'use strict';

  // This script's own <script> tag is intentionally emitted from more
  // than one place (snippets/head.liquid — the guaranteed-to-execute
  // copy, see its own comment — AND snippets/cart-line-items.liquid,
  // kept as a harmless belt-and-suspenders for any future non-templated
  // surface). customElements.define() below is self-guarding, but
  // armNativeDismantleWatcher() attaches a plain document-level click
  // listener with no such guard — running the whole IIFE more than once
  // would attach it more than once, and a single "X" click would then
  // queue and process the SAME restore N times (confirmed live: 3
  // qty of the restored product after a 3-line Cas E merge + remove,
  // from 3 separately-executed copies of this script all reacting to
  // one click). window.CartBundle only ever gets assigned once this far
  // into a successful run, so its presence is this script's own signal
  // that it has already fully initialized on this page.
  if (window.CartBundle) return;

  /**
   * <cart-bundle-toggle>
   *
   * Behaviors depending on which branch snippets/cart-bundle-toggle.liquid
   * rendered (see its own doc comment for the full rationale):
   *
   *  - [data-bundle-applied] (Branch A — this line IS the trio, the max
   *    tier): the switch is always on. Clicking it removes this line and
   *    restores the customer's "first product" (see below) — the exact
   *    same restore fires automatically when a bundle line (duo or trio,
   *    marked [data-bundle-line] on its .cart__item — see
   *    armNativeDismantleWatcher() below) is removed via the cart's own
   *    native "X", but NEVER via the "−" quantity stepper, which stays a
   *    plain decrement with no restore even at 0 (explicit requirement).
   *    "−" reaching 0 DOES still clear the memorized first product, just
   *    without re-adding it — see clearFirstProductIfSet() below for why
   *    that's not the same thing as a restore.
   *
   *  - otherwise (Branch B or C — this line matches ≥1 bundle, or is a
   *    duo with an upgrade available): the switch opens a routine-picker
   *    modal. Picking a routine there swaps exactly one unit of this line
   *    to that bundle's own variant. If [data-set-first-product] is
   *    present (Branch B only — the only branch that fires from a
   *    genuinely standalone line), that variant id is memorized as the
   *    "first product" (set-if-unset, never overwritten) — see below.
   *
   * ---- "First product" memory ----
   *
   * The brief this implements requires that removing a routine (duo or
   * trio) restores the very FIRST standalone product the customer added,
   * from before any fusion — not the routine's own components, and not
   * an arbitrary default. The standalone line is gone by the time a
   * routine exists (fusion always removes it), so this can't live as a
   * property on any one line — it has to survive independently of which
   * line currently exists.
   *
   * Chosen mechanism: a CART-level attribute, '_routine_first_variant_id'
   * (read via plain GET /cart.js, written via POST /cart/update.js — the
   * only Ajax Cart API endpoint that accepts cart-level `attributes`;
   * neither /cart/add.js nor /cart/change.js do). This is the most robust
   * option available and the most consistent with the rest of this file:
   *   - It survives an unlimited chain of line replacements (simple →
   *     duo → trio, or straight to trio), unlike a line property, which
   *     is only ever set on ONE line and is destroyed the moment that
   *     line's quantity drops to 0 — exactly what happens at every fusion
   *     step here. This is why the theme's previous per-line
   *     '_bundle_previous_variant_id' property (removed by this rewrite)
   *     only ever worked one step back (duo → trio), never further.
   *   - It's written/read with the exact same fetch-based helpers
   *     already used everywhere else in this file for cartChange/cartAdd,
   *     just against a third Ajax Cart API endpoint.
   *   - Writes always merge onto a freshly-fetched cart.attributes object
   *     rather than sending only the one changed key, so unrelated cart
   *     attributes this theme already uses elsewhere (the gift note, the
   *     accepted-terms checkbox — see snippets/cart-drawer.liquid) are
   *     never clobbered, regardless of whether /cart/update.js's own
   *     `attributes` parameter merges or replaces server-side.
   *   - "Set if unset" (never overwritten once present) is what makes the
   *     "very first" product stick through a duo → trio upgrade: only
   *     Branch B (the only branch reachable from a genuinely standalone
   *     line) ever calls setFirstProductIfUnset(); Branch C's duo → trio
   *     upgrade never touches it, so the original memory (or its correct
   *     absence, for a routine added directly with no origin product)
   *     carries through untouched.
   *   - Cleared back to '' the moment it's consumed by a restore, so a
   *     fully-removed routine doesn't leave a stale memory behind for an
   *     unrelated future merge.
   *
   * Neither raw /cart/change.js, /cart/add.js nor /cart/update.js is part
   * of the theme's own AJAX cart flow (that only covers quantity +/- and
   * remove, via theme.js's own cart-element class — off-limits to edit),
   * so all three mutations are done here directly against Shopify's Ajax
   * Cart API, then followed by a single combined refresh:
   *
   *  1. Manually replaces the relevant cart-element ancestor's
   *     [data-items-holder], [data-cart-price-holder] and
   *     [data-cart-total-price] with a fresh fetch of
   *     `{cart_url}?section_id=api-cart-items` (plus [data-cart-items-qty]
   *     via a small separate `{cart_url}.js` fetch, since item COUNT
   *     genuinely changes on a restore, unlike a same-count swap) —
   *     needed on both the drawer AND the /cart page, since theme.js's
   *     own cart-element only auto-refreshes these on the drawer
   *     (isCartDrawer).
   *  2. Then dispatches 'theme:cart:refresh' (bubbles), the same event
   *     theme.js's own product-form dispatches after a native add-to-
   *     cart, so the drawer's own cart-element (redundant but harmless
   *     there) and the other passive listeners (cart-progress-tiers.js,
   *     cart-checkout-price.js) stay in sync too.
   *
   * assets/cart-bundle-merge-banner.js (Cas D/E, the cart-level "complete
   * the routine" banner for ≥2 already-separate simple lines) reuses
   * cartChange/cartAdd/fetchCart/refreshCart/setFirstProductIfUnset from
   * here via window.CartBundle, rather than duplicating them — loaded
   * right after this file (see snippets/cart-line-items.liquid), so this
   * module-level object always exists by the time it runs.
   */
  const CART_REFRESH_EVENTS = ['theme:cart:refresh'];
  const FIRST_PRODUCT_ATTRIBUTE = '_routine_first_variant_id';

  function cartChange(id, quantity) {
    return fetch(window.theme.routes.cart_change_url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({id, quantity}),
    }).then((response) => response.json());
  }

  function cartAdd(items) {
    return fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({items}),
    }).then((response) => response.json());
  }

  function cartUpdateAttributes(attributes) {
    return fetch(window.theme.routes.cart_update_url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({attributes}),
    }).then((response) => response.json());
  }

  function fetchCart() {
    return fetch(window.theme.routes.cart_url + '.js').then((response) => response.json());
  }

  /**
   * Restores the memorized first product, if any, and clears the memory.
   * Resolves `true` if a restore actually happened (caller should
   * refresh), `false` if there was nothing to restore (a routine added
   * directly, with no origin product — caller should leave the cart's
   * own already-current state alone).
   */
  function restoreFirstProduct(prefetchedCart) {
    return (prefetchedCart ? Promise.resolve(prefetchedCart) : fetchCart()).then((cart) => {
      const variantId = cart.attributes && cart.attributes[FIRST_PRODUCT_ATTRIBUTE];
      if (!variantId) return false;

      const clearedAttributes = Object.assign({}, cart.attributes, {[FIRST_PRODUCT_ATTRIBUTE]: ''});
      return cartAdd([{id: Number(variantId), quantity: 1}])
        .then(() => cartUpdateAttributes(clearedAttributes))
        .then(() => true);
    });
  }

  /**
   * Memorizes `variantId` as the first product, but only if nothing is
   * memorized yet — see the doc comment above for why this must never
   * overwrite an existing value.
   */
  function setFirstProductIfUnset(variantId, prefetchedCart) {
    return (prefetchedCart ? Promise.resolve(prefetchedCart) : fetchCart()).then((cart) => {
      if (cart.attributes && cart.attributes[FIRST_PRODUCT_ATTRIBUTE]) return false;

      const attributes = Object.assign({}, cart.attributes, {[FIRST_PRODUCT_ATTRIBUTE]: String(variantId)});
      return cartUpdateAttributes(attributes).then(() => true);
    });
  }

  /**
   * Clears the memorized first product WITHOUT re-adding it — used only
   * when the "−" stepper drives a bundle line to 0 (see
   * armNativeDismantleWatcher() below). The "−" must never restore the
   * product (explicit brief requirement, unchanged), but leaving the
   * attribute set here would orphan it: it would point at a line that no
   * longer exists, and set-if-unset would then block the NEXT unrelated
   * product's own memory from ever being recorded — confirmed live: add
   * A, fuse it, "−" the routine to 0 (attribute stays set on A, no line
   * left), add a different B, fuse it too (setFirstProductIfUnset sees
   * the stale A and silently refuses to update), then "X" restores A
   * instead of B, with nothing telling the customer why. Clearing here
   * closes that gap — no attribute should ever outlive the line it
   * refers to, regardless of which path removed that line.
   */
  function clearFirstProductIfSet() {
    return fetchCart().then((cart) => {
      if (!cart.attributes || !cart.attributes[FIRST_PRODUCT_ATTRIBUTE]) return false;

      const clearedAttributes = Object.assign({}, cart.attributes, {[FIRST_PRODUCT_ATTRIBUTE]: ''});
      return cartUpdateAttributes(clearedAttributes).then(() => true);
    });
  }

  /**
   * Cas D/E's merge banner, and every JS-driven line replacement in this
   * file (routine picker pick, trio uncheck), all end in the same place:
   * refreshCart() throws away and rebuilds [data-items-holder]'s entire
   * contents (see its own doc comment) — the freshly-inserted nodes get
   * a CSS `animation` (assets/cart-drawer.css, .cart-drawer .cart__item
   * etc.) that fades/settles them in, but a `transition` can't do
   * anything for a node that's about to be destroyed outright: this is
   * the "before" half of that pair, a genuine exit for the SPECIFIC
   * element(s) this action is about to remove or replace, played while
   * they're still real, connected DOM nodes — fully-native flows this
   * file doesn't control (the "−" stepper, the "X" that isn't sitting on
   * a bundle line) don't get this, since theme.js's own remove/quantity
   * handling (off-limits to edit) already runs before this file ever
   * gets a chance to react.
   */
  const LEAVE_MS = 200;

  function fadeOutElements(elements) {
    const targets = elements.filter(Boolean);
    if (!targets.length) return Promise.resolve();

    targets.forEach((el) => {
      el.style.transition = `opacity ${LEAVE_MS}ms ease, transform ${LEAVE_MS}ms ease`;
      el.style.opacity = '0';
      el.style.transform = 'scale(0.97)';
    });

    return new Promise((resolve) => setTimeout(resolve, LEAVE_MS));
  }

  /**
   * `el.innerHTML = html` never executes any <script> tag the new markup
   * contains — a plain browser limitation, not specific to this theme.
   * Harmless for every OTHER script this feature has ever injected this
   * way (cart-bundle-toggle.js itself, cart-bundle-merge-banner.js)
   * because customElements.define() only needs to succeed once per page
   * for a given tag — the browser then auto-upgrades every current and
   * future matching element regardless of how it was inserted. It broke
   * outright for cart-bundle-merge-banner.js specifically the first time
   * this branch (Cas D/E) was live-tested: its very first appearance in
   * the DOM happened to come through exactly this innerHTML swap, before
   * that class had ever been defined any other way, so its switch was
   * inert on click with no console error. Same fix theme.js's own
   * CartElement.setInnerHTML() already uses elsewhere in this theme
   * (off-limits to edit, but safe and correct to mirror here): after
   * setting innerHTML, replace each <script> with a freshly created one
   * carrying the same attributes, which DOES execute.
   */
  function executeScripts(container) {
    container.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function refreshCart(scope) {
    scope = scope || document;
    const itemsHolder = scope.querySelector('[data-items-holder]');
    const priceHolder = scope.querySelector('[data-cart-price-holder]');

    return fetch(window.theme.routes.cart_url + '?section_id=api-cart-items')
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const lineItems = doc.querySelector('[data-api-line-items]');
        const cartPrice = doc.querySelector('[data-api-cart-price]');

        if (itemsHolder) {
          itemsHolder.innerHTML = lineItems ? lineItems.innerHTML : '';
          itemsHolder.classList.toggle('hidden', !lineItems);
          executeScripts(itemsHolder);
        }

        if (priceHolder && cartPrice) {
          priceHolder.innerHTML = cartPrice.innerHTML;
        }

        // [data-cart-total-price] (the checkout button's own price, NOT
        // inside [data-cart-price-holder]) is otherwise only written by
        // theme.js's cart-element on 'theme:cart:refresh' — drawer-only,
        // same gap as above. cart-checkout-price.js's own listener
        // covers the adjacent compare-at price independently, so this
        // only needs the plain current total.
        const totalPriceEl = scope.querySelector('[data-cart-total-price]');
        const subtotalCents = Number(doc.querySelector('[data-checkout-price-data]')?.dataset.subtotal);

        if (totalPriceEl && !Number.isNaN(subtotalCents) && window.theme?.formatMoney) {
          const currencyEnable = window.theme?.settings?.currency_code_enable ?? false;
          const format = currencyEnable ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
          totalPriceEl.textContent = window.theme.formatMoney(subtotalCents, format);
        }

        // [data-cart-items-qty] (the "(N)" next to "Mon panier"/cart
        // title, both surfaces): a swap is always -1 line +1 line (net
        // quantity unchanged) but a restore nets +1 unit overall —
        // api-cart-items carries no item_count, so this needs cart.js
        // separately.
        return fetch(window.theme.routes.cart_url + '.js')
          .then((response) => response.json())
          .then((cart) => {
            scope.querySelectorAll('[data-cart-items-qty]').forEach((el) => {
              el.textContent = `${cart.item_count} `;
              el.classList.toggle('hidden', cart.item_count === 0);
            });
          });
      })
      .then(() => {
        CART_REFRESH_EVENTS.forEach((eventName) => {
          document.dispatchEvent(new CustomEvent(eventName, {bubbles: true}));
        });
      });
  }

  /**
   * Watches the cart's OWN native "X" (delete) link for a bundle line
   * (duo or trio, [data-bundle-line] on its .cart__item — see snippets/
   * cart-line-items.liquid) and restores the first product afterwards,
   * same as clicking that line's own Branch A switch (trio only) would.
   *
   * Deliberately NOT wired to restore anything from the "−" quantity
   * stepper reaching 0 — the brief this implements explicitly excludes
   * it: "-" always stays a plain decrement, even down to 0, never a
   * restore trigger. It IS wired to clear-only (see
   * clearFirstProductIfSet() above) when "−" drives a bundle line to 0,
   * so the memory never outlives its line regardless of which control
   * removed it.
   *
   * Module-level (not per-instance): the removed line is destroyed by
   * the native flow's own DOM replacement before this can run, so which
   * line to act on is captured up front, at click time, while it's still
   * attached — nothing about the line itself is needed afterwards since
   * both actions are entirely attribute-driven, not derived from the
   * line. Both listeners run in the capture phase, ahead of the native
   * quantity-counter/remove-link handlers wherever they're registered,
   * so reading the quantity field's value at click time is always the
   * PRE-click value.
   */
  let pendingRestore = false;
  let pendingClear = false;

  function queueRestore(cartItemEl) {
    if (!cartItemEl?.hasAttribute('data-bundle-line')) return;
    pendingRestore = {scope: cartItemEl.closest('cart-element') || document};
  }

  function queueClearOnZero(cartItemEl) {
    if (!cartItemEl?.hasAttribute('data-bundle-line')) return;
    const field = cartItemEl.querySelector('[data-quantity-field]');
    const currentQuantity = field ? parseInt(field.value, 10) : NaN;
    // Only the click that actually drives quantity 1 → 0 matters here;
    // theme.js clamps the field at its own minValue (usually 0) so a
    // click at quantity 1 is always the one that removes the line.
    if (currentQuantity !== 1) return;
    pendingClear = true;
  }

  function processPendingRestore() {
    if (!pendingRestore) return;
    const {scope} = pendingRestore;
    pendingRestore = null;

    restoreFirstProduct()
      .then((restored) => (restored ? refreshCart(scope) : null))
      .catch((error) => console.error('cart-bundle-toggle:', error));
  }

  function processPendingClear() {
    if (!pendingClear) return;
    pendingClear = false;

    clearFirstProductIfSet().catch((error) => console.error('cart-bundle-toggle:', error));
  }

  function armNativeDismantleWatcher() {
    // The delete link always removes the line outright, regardless of
    // its current quantity — no need to check quantity first.
    document.addEventListener(
      'click',
      (event) => {
        const removeLink = event.target.closest('[data-item-remove]');
        if (removeLink) {
          queueRestore(event.target.closest('[data-cart-item]'));
          return;
        }

        const minusButton = event.target.closest('[data-quantity-minus]');
        if (!minusButton) return;
        queueClearOnZero(event.target.closest('[data-cart-item]'));
      },
      true
    );

    ['theme:cart:refresh', 'theme:cart:added'].forEach((eventName) => {
      document.addEventListener(eventName, processPendingRestore);
      document.addEventListener(eventName, processPendingClear);
    });
  }

  armNativeDismantleWatcher();

  window.CartBundle = {
    cartChange,
    cartAdd,
    cartUpdateAttributes,
    fetchCart,
    refreshCart,
    restoreFirstProduct,
    setFirstProductIfUnset,
    fadeOutElements,
  };

  customElements.get('cart-bundle-toggle') ||
    customElements.define(
      'cart-bundle-toggle',
      class extends HTMLElement {
        connectedCallback() {
          this.switchButton = this.querySelector('[data-bundle-switch]');
          this.isApplied = this.hasAttribute('data-bundle-applied');

          if (this.isApplied) {
            this.boundReverse = this.handleReverseClick.bind(this);
            this.switchButton?.addEventListener('click', this.boundReverse);
            return;
          }

          this.modal = this.querySelector('[data-bundle-modal]');
          this.boundOpen = this.openModal.bind(this);
          this.boundClose = this.closeModal.bind(this);
          this.boundKeydown = this.handleKeydown.bind(this);
          this.boundAddClick = this.handleAddClick.bind(this);

          this.switchButton?.addEventListener('click', this.boundOpen);
          this.modal?.querySelector('[data-bundle-modal-backdrop]')?.addEventListener('click', this.boundClose);
          this.modal?.querySelector('[data-bundle-modal-close]')?.addEventListener('click', this.boundClose);
          this.modal?.querySelector('[data-bundle-modal-cancel]')?.addEventListener('click', this.boundClose);

          this.modal?.querySelectorAll('[data-bundle-routine-add]').forEach((button) => {
            button.addEventListener('click', this.boundAddClick);
          });
        }

        disconnectedCallback() {
          this.switchButton?.removeEventListener('click', this.boundReverse);
          this.switchButton?.removeEventListener('click', this.boundOpen);
          document.removeEventListener('keydown', this.boundKeydown);
        }

        // ---- Branch B / C: modal ----

        openModal() {
          if (!this.modal || this.isLoading) return;
          this.modal.hidden = false;
          document.addEventListener('keydown', this.boundKeydown);
          this.modal.querySelector('[data-bundle-modal-close]')?.focus();
        }

        closeModal() {
          if (!this.modal) return;
          this.modal.hidden = true;
          document.removeEventListener('keydown', this.boundKeydown);
          this.switchButton?.focus();
        }

        handleKeydown(event) {
          if (event.key === 'Escape') this.closeModal();
        }

        handleAddClick(event) {
          const card = event.target.closest('[data-bundle-routine-card]');
          const variantId = card?.dataset.bundleVariantId;
          if (!variantId) return;
          this.swapToBundle(variantId);
        }

        swapToBundle(bundleVariantId) {
          if (this.isLoading) return;
          this.setLoading(true);

          const lineKey = this.dataset.lineKey;
          const quantity = parseInt(this.dataset.lineQuantity, 10) || 1;
          const remaining = quantity - 1;

          // Branch B only: memorize this standalone line's own variant as
          // the "first product", set-if-unset. Branch C (duo → trio
          // upgrade) never carries data-set-first-product, so this is a
          // no-op there — the existing memory (or its correct absence)
          // carries through untouched.
          const firstProductVariantId = this.dataset.setFirstProduct;
          const cartItem = this.previousElementSibling?.matches('.cart__item') ? this.previousElementSibling : null;

          this.closeModal();

          fadeOutElements([cartItem, this])
            .then(() => cartChange(lineKey, remaining))
            .then(() => cartAdd([{id: Number(bundleVariantId), quantity: 1}]))
            .then(() => (firstProductVariantId ? setFirstProductIfUnset(firstProductVariantId) : null))
            .then(() => refreshCart(this.closest('cart-element') || document))
            .catch((error) => console.error('cart-bundle-toggle:', error))
            .finally(() => this.setLoading(false));
        }

        // ---- Branch A: reverse ----

        handleReverseClick() {
          if (this.isLoading) return;
          this.setLoading(true);

          const lineKey = this.dataset.lineKey;
          const scope = this.closest('cart-element') || document;
          const cartItem = this.previousElementSibling?.matches('.cart__item') ? this.previousElementSibling : null;

          fadeOutElements([cartItem, this])
            .then(() => cartChange(lineKey, 0))
            .then(() => restoreFirstProduct())
            .then(() => refreshCart(scope))
            .catch((error) => console.error('cart-bundle-toggle:', error))
            .finally(() => this.setLoading(false));
        }

        // ---- Shared ----

        setLoading(isLoading) {
          this.isLoading = isLoading;
          this.classList.toggle('is-loading', isLoading);
          this.switchButton?.toggleAttribute('disabled', isLoading);
          this.modal?.querySelectorAll('[data-bundle-routine-add]').forEach((button) => {
            button.toggleAttribute('disabled', isLoading);
          });
        }
      }
    );
})();
